import React, { createContext } from "react";
import GetDataHelper from "../helpers/GetDataHelper";
import useApi from "../hooks/useApi";
import SessionApiClient from "../api/session";
import * as sodium from "libsodium-wrappers";
import { useSelector, useDispatch } from "react-redux";
import { setSession } from "../store/sessionSlice";
import PropTypes from "prop-types";
import { internalRoutes } from "../routes";
import { useLocation } from "react-router-dom";

const AppContext = createContext();

function AppProvider({ children }) {
  const empresaUID = GetDataHelper.getEmpresaUid();
  const usuarioUID = GetDataHelper.getUsuarioUID();
  const baseUrl = GetDataHelper.getBaseUrl();
  const orangiuzWhatsApp = GetDataHelper.getOrangiuzWhatsApp();

  const location = useLocation();
  const pathname = location.pathname;
  const dispatch = useDispatch();
  const isLoading = useSelector((state) => state.loading.isLoading);
  const session = useSelector((state) => state.session.session);

  const { request: getSession, data: sessionResponse } = useApi(
    SessionApiClient.getSession,
    false
  );

  const descriptografarESetSession = React.useCallback(
    (encryptedData, nonce, chave) => {
      try {
        const dadosDescriptografados = sodium.crypto_secretbox_open_easy(
          sodium.from_hex(encryptedData),
          sodium.from_hex(nonce),
          chave
        );
        dispatch(
          setSession(JSON.parse(sodium.to_string(dadosDescriptografados)))
        );
      } catch (e) {
        localStorage.removeItem("session");
        getSession();
        // CommonSwal({
        //   title: "Oops!",
        //   text:
        //     "Ocorreu algum erro inesperado ao buscar os dados da página. Recarregue a página.",
        //   icon: "error",
        // });
      }
    },
    [dispatch, getSession]
  );

  const handleStorageChange = React.useCallback(() => {
    try {
      const sessionData = JSON.parse(localStorage.getItem("session"));
      if (sessionData)
        descriptografarESetSession(
          sessionData.data,
          sessionData.nonce,
          process.env.REACT_APP_PHP_KEY
        );
    } catch (error) {
      console.error("Erro ao acessar armazenamento local:", error);
    }
  }, [descriptografarESetSession]);

  //Adiciona a classe no_padding ao corpo do documento
  React.useEffect(() => {
    document.body.classList.add("no_padding");
  }, []);

  //Busca dados da sessão ao renderizar tela
  React.useEffect(() => {
    const sessao = localStorage.getItem("session");
    if (!sessao) getSession();
  }, [getSession]);

  //Define o retorno do backend na sessão
  React.useEffect(() => {
    if (!sessionResponse) return;

    if (typeof sessionResponse !== "object") {
      console.error("sessionResponse não é um objeto:", sessionResponse);
      localStorage.removeItem("session");
      getSession();
      return;
    }

    if (!["nonce", "data"].some((key) => key in sessionResponse)) {
      localStorage.removeItem("session");
      getSession();
      return;
    }

    localStorage.setItem("session", JSON.stringify(sessionResponse));
    window.dispatchEvent(new Event("storage"));
  }, [getSession, sessionResponse]);

  //Define no estado session a sessão armazenada
  React.useEffect(() => {
    handleStorageChange();
    window.addEventListener("storage", handleStorageChange);
    return () => {
      window.removeEventListener("storage", handleStorageChange);
    };
  }, [handleStorageChange]);

  //Verifica validade da sessão
  React.useEffect(() => {
    if (Object.keys(session).length > 0) {
      const timeSinceLastRegenerate =
        Math.floor(Date.now() / 1000) - session.__ci_last_regenerate;
      const regenerationInterval = 7200;
      if (
        usuarioUID !== session.user_guid ||
        empresaUID !== session.empresa_uid ||
        timeSinceLastRegenerate >= regenerationInterval
      ) {
        localStorage.removeItem("session");
        getSession();
      }
    }
  }, [getSession, empresaUID, usuarioUID, session]);

  //Verifica se empresa está congelada
  React.useEffect(() => {
    const newRoutes = [];
    internalRoutes.map((route) => {
      newRoutes.push(`${route.path}`);
    });
    if (newRoutes.includes(pathname) && session?.congelada === "true")
      window.location.href = `${baseUrl}logout`;
  }, [baseUrl, pathname, session?.congelada]);

  return (
    <AppContext.Provider
      value={{
        usuarioUID,
        baseUrl,
        empresaUID,
        orangiuzWhatsApp,
        session,
        getSession: getSession,
      }}
    >
      {isLoading ? (
        <div style={loadingStyle}>
          <div className="swal2-actions swal2-loading"></div>
        </div>
      ) : null}
      {children}
    </AppContext.Provider>
  );
}

const loadingStyle = {
  background: "rgba(0, 0, 0, .4)",
  position: "fixed",
  top: 0,
  right: 0,
  bottom: 0,
  left: 0,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  zIndex: 1060,
};

AppProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export { AppContext, AppProvider };
